import { FormDataShape } from '../hooks/useCheckoutController/useCheckoutController.types';

type AutofillableField = Extract<
  keyof FormDataShape,
  | 'fullName'
  | 'email'
  | 'phoneNumber'
  | 'postalCode'
  | 'state'
  | 'city'
  | 'neighborhood'
  | 'address'
  | 'number'
  | 'complement'
>;


const PARAM_TO_FIELD: Readonly<Record<string, AutofillableField>> = {
  name: 'fullName',
  email: 'email',
  phone: 'phoneNumber',
  zipCode: 'postalCode',
  postalCode: 'postalCode',
  street: 'address',
  address: 'address',
  number: 'number',
  complement: 'complement',
  district: 'neighborhood',
  neighborhood: 'neighborhood',
  city: 'city',
  state: 'state',
};


function sanitizeText(value: string): string {
  return value
    .replace(/<[^>]*>/g, '')
    .replace(/&[a-zA-Z#0-9]+;/g, ' ')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '')
    .trim();
}


const SANITIZERS: Readonly<Record<AutofillableField, (v: string) => string>> = {
  fullName: (v) =>
    sanitizeText(v)
      .replace(/[<>"'`]/g, '')
      .slice(0, 80),

  email: (v) => {
    const cleaned = sanitizeText(v).toLowerCase().replace(/\s/g, '').slice(0, 60);
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned) ? cleaned : '';
  },

  phoneNumber: (v) =>
    sanitizeText(v)
      .replace(/[^0-9+\-() ]/g, '')
      .slice(0, 25),

  postalCode: (v) =>
    sanitizeText(v)
      .replace(/[^A-Za-z0-9\- ]/g, '')
      .slice(0, 10),

  state: (v) =>
    sanitizeText(v)
      .replace(/[^A-Za-z\u00C0-\u024F ]/g, '')
      .slice(0, 50),

  city: (v) =>
    sanitizeText(v)
      .replace(/[^A-Za-z0-9\u00C0-\u024F .'\- ]/g, '')
      .slice(0, 60),

  neighborhood: (v) =>
    sanitizeText(v)
      .replace(/[<>"'`]/g, '')
      .slice(0, 80),

  address: (v) =>
    sanitizeText(v)
      .replace(/[<>"'`]/g, '')
      .slice(0, 100),

  number: (v) =>
    sanitizeText(v)
      .replace(/[^A-Za-z0-9\-]/g, '')
      .slice(0, 10),

  complement: (v) =>
    sanitizeText(v)
      .replace(/[<>"'`]/g, '')
      .slice(0, 60),
};

export function parseAutofillParams(): Partial<Pick<FormDataShape, AutofillableField>> {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);
  const result: Partial<Pick<FormDataShape, AutofillableField>> = {};

  params.forEach((rawValue, paramName) => {
    const field = PARAM_TO_FIELD[paramName];
    if (!field) return;

    const sanitized = SANITIZERS[field](rawValue);
    if (sanitized) {
      result[field] = sanitized;
    }
  });

  return result;
}
